'use client';
import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getProjectsDB, getHutangListDB, getPiutangListDB } from '@/lib/supabaseData';
import Link from 'next/link';

interface ChartRow {
  name: string;
  hutang: number;
  piutang: number;
}

const formatYAxis = (value: number) => {
  if (value >= 1000000000) return `${(value / 1000000000).toFixed(0)}M`;
  if (value >= 1000000) return `${(value / 1000000).toFixed(0)}jt`;
  return `${value}`;
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-xl p-3 text-sm">
      <p className="font-700 text-gray-800 mb-2">{label}</p>
      {payload.map((entry) => (
        <div key={`tooltip-${entry.name}`} className="flex items-center gap-2 mb-1">
          <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-500">{entry.name}:</span>
          <span className="font-600 tabular-nums text-gray-800">
            {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function HutangPiutangChart() {
  const [chartData, setChartData] = useState<ChartRow[]>([]);

  useEffect(() => {
    Promise.all([getProjectsDB(), getHutangListDB(), getPiutangListDB()]).then(([projects, hutangList, piutangList]) => {
      const activeStatuses = ['Berjalan', 'Persiapan'];
      const activeProjects = projects.filter((p) => activeStatuses.includes(p.status));

      const rows: ChartRow[] = activeProjects.map((project) => {
        const hutang = hutangList.filter((h) => h.projectId === project.id).reduce((sum, h) => sum + h.sisaHutang, 0);
        const piutang = piutangList.filter((p) => p.projectId === project.id).reduce((sum, p) => sum + p.sisaPiutang, 0);
        const shortName = project.nama.length > 8 ? project.nama.substring(0, 8) + '…' : project.nama;
        return { name: shortName, hutang, piutang };
      });

      setChartData(rows);
    });
  }, []);

  return (
    <div className="bg-white rounded-2xl p-5" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)' }}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-base font-700 text-gray-800">Hutang vs Piutang per Proyek</h3>
          <p className="text-xs text-gray-400 mt-0.5">Perbandingan nilai dalam Rupiah</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1.5 rounded-lg bg-[#1a3a6b] text-white text-xs font-600">Bulanan</button>
          <button className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-500 text-xs font-600 hover:bg-gray-200 transition-colors">Tahunan</button>
        </div>
      </div>
      <div className="h-60">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }} barCategoryGap="35%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '12px', color: '#6b7280' }}
            />
            <Bar dataKey="hutang" name="Hutang" fill="#ef4444" radius={[4, 4, 0, 0]} />
            <Bar dataKey="piutang" name="Piutang" fill="#1a3a6b" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-50">
        <Link href="/laporan" className="text-sm font-600 text-[#1a3a6b] hover:underline flex items-center gap-1">
          Lihat Detail →
        </Link>
      </div>
    </div>
  );
}